import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../App';
import { api } from '../api';
import { toast } from 'sonner';
import { Plane, Building2, Save, CheckCircle, Calendar, Copy } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { DatePicker, TimePicker } from '../components/DateTimePicker';
import PageLayout from '../components/PageLayout';
import InfoBox from '../components/InfoBox';
import { useSiteSettings } from '../SiteSettingsContext';

const AIRLINES = [
  'American Airlines', 'Delta Air Lines', 'United Airlines', 'Southwest Airlines',
  'JetBlue Airways', 'Alaska Airlines', 'Spirit Airlines', 'Frontier Airlines',
  'Avianca', 'Copa Airlines', 'Volaris', 'Other'
];

const AIRPORTS = [
  { code: 'MSP', name: 'Minneapolis–St. Paul' },
  { code: 'ORD', name: 'Chicago O\'Hare' },
  { code: 'DFW', name: 'Dallas/Fort Worth' },
  { code: 'ATL', name: 'Atlanta' },
  { code: 'IAH', name: 'Houston' },
  { code: 'MIA', name: 'Miami' },
  { code: 'LAX', name: 'Los Angeles' },
  { code: 'DEN', name: 'Denver' },
  { code: 'JFK', name: 'New York JFK' },
  { code: 'EWR', name: 'Newark' },
  { code: 'SFO', name: 'San Francisco' },
  { code: 'SEA', name: 'Seattle' },
  { code: 'BOS', name: 'Boston' },
  { code: 'OTHER', name: 'Other / Not Listed' },
];

const emptyFlight = {
  arrival_date: '', arrival_time: '', arrival_airline: '',
  arrival_flight_number: '', arrival_origin: '',
  departure_date: '', departure_time: '', departure_airline: '',
  departure_flight_number: ''
};

const FlightHotelPage = () => {
  const { user, isAdmin } = useAuth();
  const { infoBoxes } = useSiteSettings();
  const [searchParams] = useSearchParams();

  // Admin edit-on-behalf-of support. When ?edit_user=<id> is present AND the
  // logged-in user is an admin, all fetches + saves target that user_id
  // instead of the admin's own account.
  const editUserId = searchParams.get('edit_user');
  const adminMode = !!editUserId && isAdmin && editUserId !== user?.user_id;
  const targetUserId = adminMode ? editUserId : user?.user_id;
  const [targetUserSummary, setTargetUserSummary] = useState(null);

  const [trips, setTrips] = useState([]);
  const [selectedTripId, setSelectedTripId] = useState(searchParams.get('trip_id') || '');
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const defaultTab = searchParams.get('tab') || 'flight';

  // Flight state
  const [flightInfo, setFlightInfo] = useState({ ...emptyFlight });
  const [flightSaved, setFlightSaved] = useState(false);
  const [savingFlight, setSavingFlight] = useState(false);
  const [siblingFlights, setSiblingFlights] = useState({});

  // Hotel state
  const [roommatePreference, setRoommatePreference] = useState('');
  const [hotelSaved, setHotelSaved] = useState(false);
  const [savingHotel, setSavingHotel] = useState(false);

  useEffect(() => {
    // In admin mode we can't use /my-trips (that would be the admin's trips).
    // Fetch trips the target user is on via /trips + filter by their assignments.
    if (adminMode) {
      Promise.all([
        api.get('/trips').catch(() => ({ data: [] })),
        api.get(`/admin/participants/${targetUserId}/full-profile`).catch(() => ({ data: null })),
      ]).then(([tripsRes, profRes]) => {
        const target = profRes.data || {};
        setTargetUserSummary(target);
        const targetTripIds = new Set((target.trips || []).map(t => t.trip?.trip_id).filter(Boolean));
        const targetTrips = (tripsRes.data || []).filter(t => targetTripIds.has(t.trip_id));
        setTrips(targetTrips);
        if (!selectedTripId && targetTrips.length > 0) {
          setSelectedTripId(targetTrips[0].trip_id);
        } else if (targetTrips.length === 0) {
          setLoading(false);
        }
      }).catch(() => setLoading(false));
      return;
    }
    api.get('/my-trips').then(async (res) => {
      const myTrips = res.data || [];
      setTrips(myTrips);
      if (!selectedTripId && myTrips.length > 0) {
        const upcoming = [...myTrips].sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
          .find(t => new Date(t.start_date) >= new Date());
        setSelectedTripId((upcoming || myTrips[0]).trip_id);
      } else if (myTrips.length === 0) {
        setLoading(false);
      }
      // Pre-fetch flight data for all trips (for copy feature)
      if (myTrips.length > 1) {
        const flights = {};
        for (const t of myTrips) {
          try {
            const fRes = await api.get(`/flights/${t.trip_id}/${user?.user_id}`);
            if (fRes.data && fRes.data.arrival_date) flights[t.trip_id] = fRes.data;
          } catch {}
        }
        setSiblingFlights(flights);
      }
    }).catch(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedTripId || !targetUserId) return;
    setLoading(true);
    setFlightSaved(false);
    setHotelSaved(false);

    const trip = trips.find(t => t.trip_id === selectedTripId);
    setSelectedTrip(trip || null);

    Promise.all([
      api.get(`/flights/${selectedTripId}/${targetUserId}`).catch(() => ({ data: null })),
      api.get(`/hotel-preferences/${selectedTripId}/${targetUserId}`).catch(() => ({ data: null }))
    ]).then(([flightRes, hotelRes]) => {
      if (flightRes.data && Object.keys(flightRes.data).length > 1) {
        setFlightInfo({ ...emptyFlight, ...flightRes.data });
        setFlightSaved(true);
      } else {
        setFlightInfo({ ...emptyFlight });
        setFlightSaved(false);
      }
      if (hotelRes.data) {
        setRoommatePreference(hotelRes.data.roommate_preference || '');
        setHotelSaved(!!(hotelRes.data.roommate_preference));
      } else {
        setRoommatePreference('');
        setHotelSaved(false);
      }
    }).finally(() => setLoading(false));
  }, [selectedTripId, targetUserId, trips]);

  const setFlight = (key, val) => setFlightInfo(f => ({ ...f, [key]: val }));

  const handleSaveFlight = async () => {
    if (!flightInfo.arrival_date || !flightInfo.arrival_time || !flightInfo.arrival_airline) {
      toast.error('Please fill in at least: arrival date, time, and airline');
      return;
    }
    setSavingFlight(true);
    try {
      await api.post('/flights', { ...flightInfo, trip_id: selectedTripId, user_id: targetUserId });
      toast.success('Flight information saved!');
      setFlightSaved(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save flight info'); }
    finally { setSavingFlight(false); }
  };

  const handleSaveHotel = async () => {
    setSavingHotel(true);
    try {
      await api.post('/hotel-preferences', {
        user_id: targetUserId, trip_id: selectedTripId,
        roommate_preference: roommatePreference || null,
        accommodation_notes: null
      });
      toast.success('Hotel preference saved!');
      setHotelSaved(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSavingHotel(false); }
  };

  const tripSelect = trips.length > 1 ? (
    <Select value={selectedTripId} onValueChange={setSelectedTripId}>
      <SelectTrigger className="w-56 h-9 text-sm" data-testid="trip-select">
        <SelectValue placeholder="Select trip..." />
      </SelectTrigger>
      <SelectContent>
        {trips.map(t => <SelectItem key={t.trip_id} value={t.trip_id}>{t.name}</SelectItem>)}
      </SelectContent>
    </Select>
  ) : null;

  return (
    <PageLayout title="Flight & Hotel" headerActions={tripSelect} maxWidth="max-w-3xl">
        <div className="space-y-5">
          {adminMode && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center justify-between" data-testid="admin-flight-hotel-banner">
              <div className="text-sm text-amber-800">
                <strong>Admin edit mode:</strong> editing Flight &amp; Hotel for <span className="font-semibold">{targetUserSummary?.user?.name || targetUserSummary?.profile?.first_name || 'participant'}</span> ({targetUserSummary?.user?.email || editUserId})
              </div>
              <a href="/admin/participants" className="text-xs text-amber-700 hover:underline">Return to Participants</a>
            </div>
          )}
          {selectedTrip && (
            <div className="flex items-center gap-2 text-sm text-slate-500 bg-slate-50 px-4 py-2 rounded-xl">
              <Calendar className="w-4 h-4 text-primary flex-shrink-0" />
              <span><strong>{selectedTrip.name}</strong> · {selectedTrip.start_date} – {selectedTrip.end_date} · {selectedTrip.location}</span>
            </div>
          )}

          {loading ? (
            <div className="text-center py-16"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" /></div>
          ) : trips.length === 0 ? (
            <Card className="text-center py-12">
              <CardContent>
                <Plane className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 font-medium">No trips assigned yet</p>
                <p className="text-slate-400 text-sm mt-1">You'll be able to submit flight and hotel info once you're assigned to a trip.</p>
              </CardContent>
            </Card>
          ) : (
            <Tabs defaultValue={defaultTab}>
              <TabsList className="mb-4 w-full flex-wrap sm:flex-nowrap">
                <TabsTrigger value="flight" className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm flex-1 sm:flex-none">
                  <Plane className="w-4 h-4 flex-shrink-0" />
                  <span className="hidden xs:inline">Flight</span>
                  <span className="xs:hidden">Flight</span>
                  <span className="hidden sm:inline"> Information</span>
                  {flightSaved && <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />}
                </TabsTrigger>
                <TabsTrigger value="hotel" className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm flex-1 sm:flex-none">
                  <Building2 className="w-4 h-4 flex-shrink-0" />
                  <span className="hidden xs:inline">Hotel</span>
                  <span className="xs:hidden">Hotel</span>
                  <span className="hidden sm:inline"> Preference</span>
                  {hotelSaved && <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />}
                </TabsTrigger>
              </TabsList>

              {/* ── Flight Tab ── */}
              <TabsContent value="flight" className="space-y-4">
                <InfoBox text={infoBoxes.flight} className="mb-2">
                  Arrival window: <strong>Saturday 11:00 AM – 2:00 PM</strong> at Guatemala City (GUA).
                  Shuttles to Antigua depart ~2:00 PM. Book early — typical fares $600–$1,100.
                </InfoBox>

                {/* Copy from sibling trip — show when current flight is empty and another trip has data */}
                {!flightSaved && trips.length > 1 && (() => {
                  const otherTrips = trips.filter(t => t.trip_id !== selectedTripId && siblingFlights[t.trip_id]);
                  if (otherTrips.length === 0) return null;
                  return (
                    <div className="flex flex-wrap items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-xl">
                      <Copy className="w-4 h-4 text-blue-600 flex-shrink-0" />
                      <span className="text-sm text-blue-800">Copy flight info from:</span>
                      {otherTrips.map(t => (
                        <Button
                          key={t.trip_id}
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs rounded-full border-blue-300 text-blue-700 hover:bg-blue-100"
                          onClick={() => {
                            const src = siblingFlights[t.trip_id];
                            setFlightInfo({
                              arrival_date: src.arrival_date || '',
                              arrival_time: src.arrival_time || '',
                              arrival_airline: src.arrival_airline || '',
                              arrival_flight_number: src.arrival_flight_number || '',
                              arrival_origin: src.arrival_origin || '',
                              departure_date: src.departure_date || '',
                              departure_time: src.departure_time || '',
                              departure_airline: src.departure_airline || '',
                              departure_flight_number: src.departure_flight_number || '',
                            });
                            toast.success(`Flight info copied from ${t.name} — review and save`);
                          }}
                          data-testid={`copy-flight-${t.trip_id}`}
                        >
                          {t.name?.replace(/October \d{4} - /, '') || t.name}
                        </Button>
                      ))}
                    </div>
                  );
                })()}

                <Card>
                  <CardHeader><CardTitle className="font-heading text-base">Arrival Flight</CardTitle></CardHeader>
                  <CardContent className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label>Arrival Date *</Label>
                      <DatePicker value={flightInfo.arrival_date} onChange={v => setFlight('arrival_date', v)} testId="arrival-date-input" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Arrival Time at GUA *</Label>
                      <TimePicker value={flightInfo.arrival_time} onChange={v => setFlight('arrival_time', v)} testId="arrival-time-input" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Airline *</Label>
                      <Select value={flightInfo.arrival_airline} onValueChange={v => setFlight('arrival_airline', v)}>
                        <SelectTrigger data-testid="arrival-airline-select"><SelectValue placeholder="Select airline..." /></SelectTrigger>
                        <SelectContent>{AIRLINES.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Flight Number</Label>
                      <Input value={flightInfo.arrival_flight_number} onChange={e => setFlight('arrival_flight_number', e.target.value)} placeholder="e.g. DL 1234" />
                    </div>
                    <div className="space-y-1.5 sm:col-span-2">
                      <Label>Departing From</Label>
                      <Select value={flightInfo.arrival_origin} onValueChange={v => setFlight('arrival_origin', v)}>
                        <SelectTrigger><SelectValue placeholder="Departure airport..." /></SelectTrigger>
                        <SelectContent>{AIRPORTS.map(a => <SelectItem key={a.code} value={a.code}>{a.code} — {a.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader><CardTitle className="font-heading text-base">Return Flight</CardTitle></CardHeader>
                  <CardContent className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label>Departure Date</Label>
                      <DatePicker value={flightInfo.departure_date} onChange={v => setFlight('departure_date', v)} testId="departure-date-input" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Departure Time from GUA</Label>
                      <TimePicker value={flightInfo.departure_time} onChange={v => setFlight('departure_time', v)} testId="departure-time-input" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Airline</Label>
                      <Select value={flightInfo.departure_airline} onValueChange={v => setFlight('departure_airline', v)}>
                        <SelectTrigger><SelectValue placeholder="Select airline..." /></SelectTrigger>
                        <SelectContent>{AIRLINES.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Flight Number</Label>
                      <Input value={flightInfo.departure_flight_number} onChange={e => setFlight('departure_flight_number', e.target.value)} placeholder="e.g. UA 5678" />
                    </div>
                  </CardContent>
                </Card>

                <Button onClick={handleSaveFlight} disabled={savingFlight} className="rounded-full bg-rojo hover:bg-rojo-dark w-full sm:w-auto" data-testid="save-flight-btn">
                  <Save className="w-4 h-4 mr-2" />{savingFlight ? 'Saving...' : flightSaved ? 'Update Flight Info' : 'Save Flight Info'}
                </Button>
              </TabsContent>

              {/* ── Hotel Tab ── */}
              <TabsContent value="hotel" className="space-y-4">
                <InfoBox text={infoBoxes.hotel} className="mb-2">
                  The team stays at <strong>Quinta de las Flores</strong> in Antigua.
                  <a href="https://www.quintadelasflores.com/" target="_blank" rel="noopener noreferrer" className="underline ml-1">quintadelasflores.com</a>.
                  Room assignments are made by the trip coordinator — this is your roommate preference only.
                </InfoBox>

                <Card>
                  <CardHeader>
                    <CardTitle className="font-heading text-base">Roommate Preference</CardTitle>
                    <CardDescription>Who would you like to share a room with? Enter a name, specialty, or any preference.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Textarea
                      value={roommatePreference}
                      onChange={e => setRoommatePreference(e.target.value)}
                      placeholder="e.g., I'd like to room with Frida Kahlo or anyone from the OR nurse team"
                      rows={3}
                      className="resize-none"
                      data-testid="roommate-preference-input"
                    />
                  </CardContent>
                </Card>

                <Button onClick={handleSaveHotel} disabled={savingHotel} className="rounded-full bg-rojo hover:bg-rojo-dark w-full sm:w-auto" data-testid="save-hotel-btn">
                  <Save className="w-4 h-4 mr-2" />{savingHotel ? 'Saving...' : hotelSaved ? 'Update Preference' : 'Save Preference'}
                </Button>
              </TabsContent>
            </Tabs>
          )}
        </div>
    </PageLayout>
  );
};

export default FlightHotelPage;
